import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

/** Klasa niosąca animację; jej @keyframes siedzą globalnie w app.component.scss. */
const SHAKE_CLASS = 'shake-invalid';

/**
 * Znacznik dla pól, których nie da się znaleźć po klasach Angulara — patrz
 * `appShakeInvalidRow`. Szablon wiesza go na tym, co i tak pokazuje błąd wiersza.
 */
const ROW_MARKER = 'shake-invalid-marker';

/**
 * Tylko kontrolki, nie kontener. Angular wiesza `ng-invalid`/`ng-touched` również na
 * elemencie `<form>`, a potrząsanie całym formularzem nie wskazywałoby niczego.
 */
const INVALID_CONTROLS = [
  'input.ng-invalid.ng-touched',
  'select.ng-invalid.ng-touched',
  'textarea.ng-invalid.ng-touched'
].join(', ');

/**
 * Karta pola formularzy „observator". Telefon i datę rysują w niej osobne komponenty,
 * więc ich `<input>` leży głębiej i jego rodzic nie jest już widoczną ramką pola.
 */
const FIELD_CARD = '.form-field-observator';

/**
 * Krótkie potrząśnięcie pól, które zablokowały zapis.
 *
 * Ruch prowadzi wzrok do miejsca problemu szybciej niż sam komunikat pod polem —
 * zwłaszcza w długim formularzu, gdzie niepoprawne pole bywa poza ekranem.
 *
 * Wyzwalane tokenem, a nie stanem formularza: potrząśnięcie ma się odpalić dokładnie
 * przy NIEUDANEJ PRÓBIE ZAPISU, a nie za każdym razem, gdy użytkownik wyjdzie z jeszcze
 * niewypełnionego pola w trakcie pracy. Wzorzec użycia:
 *
 *     <form [formGroup]="form" [appShakeInvalid]="shakeToken">
 *
 *     if (!this.form.valid) {
 *       this.form.markAllAsTouched();
 *       this.shakeToken++;
 *       return;
 *     }
 *
 * Sam czerwony stan pola jest niezależny od tej dyrektywy — daje go globalna reguła
 * `.ng-invalid.ng-touched` i zostaje także wtedy, gdy ruch jest wyłączony.
 */
@Directive({
  selector: '[appShakeInvalid]'
})
export class ShakeInvalidDirective implements OnChanges, OnDestroy {

  /** Licznik nieudanych prób zapisu. Każdy wzrost to jedno potrząśnięcie. */
  @Input('appShakeInvalid') trigger: number;

  /**
   * Selektor wiersza — dla ekranów, na których pole jest zwinięte do jednej linii
   * z wartością, a `<input>` trafia do DOM dopiero po jego rozwinięciu (ustawienia
   * podopiecznego). Klas `ng-invalid`/`ng-touched` nie ma wtedy na czym szukać, więc
   * niepoprawny wiersz oznacza sam szablon — elementem z klasą `shake-invalid-marker`
   * (u nas ikonką błędu przy etykiecie). Potrząsamy jego najbliższym przodkiem
   * pasującym do tego selektora, czyli całym wierszem, bo tyle widzi użytkownik.
   */
  @Input() appShakeInvalidRow: string;

  /** Uchwyt odłożonego odczytu — do anulowania, gdy formularz zniknie wcześniej. */
  private pending: any = null;

  constructor(private host: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Pierwsze ustawienie to wyświetlenie formularza, a nie nieudany zapis.
    if (!changes.trigger || changes.trigger.firstChange) {
      return;
    }

    // Klasy `ng-touched` dokłada NgControlStatus na polach, czyli na DZIECIACH tego
    // hosta — w tym samym przebiegu detekcji zmian, ale po nas. Odczyt teraz zwróciłby
    // pustą listę, więc odkładamy go na następne zadanie w pętli zdarzeń.
    this.cancelPending();
    this.ngZone.runOutsideAngular(() => {
      this.pending = setTimeout(() => {
        this.pending = null;
        this.react();
      });
    });
  }

  ngOnDestroy(): void {
    this.cancelPending();
  }

  /**
   * Bez tego odłożony odczyt dopisywał klasę do pola formularza, którego już nie ma
   * na ekranie — panel zamknięty tuż po nieudanym zapisie zostawiał wiszący timer.
   */
  private cancelPending(): void {
    if (this.pending !== null) {
      clearTimeout(this.pending);
      this.pending = null;
    }
  }

  /**
   * Element, którym faktycznie ruszamy. W tym repo widoczną ramkę i tło niesie KONTENER
   * pola, a sam `<input>` jest przezroczysty (`border: none; background: transparent`) —
   * potrząśnięcie inputem przesuwałoby więc tylko tekst wewnątrz nieruchomej ramki.
   * Ruszając kontenerem, poruszamy całym polem razem z etykietą i komunikatem błędu.
   *
   * Zabezpieczenie: nigdy nie wychodzimy powyżej hosta dyrektywy — inaczej formularz,
   * w którym input jest bezpośrednim dzieckiem `<form>`, trząsłby się w całości.
   */
  private shakeTarget(control: HTMLElement): HTMLElement {
    const card = control.closest(FIELD_CARD) as HTMLElement;
    if (card && card !== this.host.nativeElement && this.host.nativeElement.contains(card)) {
      return card;
    }

    const parent = control.parentElement;
    return parent && parent !== this.host.nativeElement ? parent : control;
  }

  /**
   * Elementy, którymi faktycznie ruszamy, w kolejności występowania w dokumencie —
   * pierwszy z nich jest tym, do którego przewijamy.
   */
  private targets(): HTMLElement[] {
    const selector = this.appShakeInvalidRow
      ? INVALID_CONTROLS + ', .' + ROW_MARKER
      : INVALID_CONTROLS;

    const targets: HTMLElement[] = [];
    const controls: HTMLElement[] = [];

    this.host.nativeElement.querySelectorAll<HTMLElement>(selector).forEach(hit => {
      const isRow = hit.classList.contains(ROW_MARKER);
      const target = isRow
        ? hit.closest(this.appShakeInvalidRow) as HTMLElement
        : this.shakeTarget(hit);

      // Wiersz z dwiema ikonkami błędu ma zadrżeć raz.
      if (!target || targets.indexOf(target) !== -1) {
        return;
      }
      targets.push(target);
      if (!isRow) {
        controls.push(target);
      }
    });

    // Pole akurat rozwinięte do edycji leży W ŚRODKU swojego wiersza. Trzęsienie obydwoma
    // dałoby animację w animacji, więc wiersz ustępuje dokładniejszemu wskazaniu.
    return targets.filter(target =>
      !controls.some(control => control !== target && target.contains(control)));
  }

  private react(): void {
    const targets = this.targets();
    if (!targets.length) {
      return;
    }

    // Sekcje ustawień przewijają się wewnątrz własnego kontenera i bywają kilka razy
    // wyższe od okna — bez tego potrząśnięcie działoby się poza ekranem. `nearest`
    // przewija najmniej, jak się da, i nie rusza niczym, gdy pole i tak widać.
    // Przewinięcie prowadzi do problemu, a nie zdobi, więc zostaje także przy
    // wyłączonym ruchu; wyłącza się tylko jego płynność.
    const reducedMotion = this.prefersReducedMotion();
    targets[0].scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });

    // Ruch wywołany interakcją musi dać się wyłączyć (WCAG 2.3.3). Komunikat i czerwony
    // stan pola zostają — znika samo potrząśnięcie.
    if (reducedMotion) {
      return;
    }

    targets.forEach(target => this.shake(target));
  }

  private shake(target: HTMLElement): void {
    target.classList.remove(SHAKE_CLASS);
    // Wymuszenie przeliczenia układu: bez tego ponowne dodanie klasy w tej samej
    // klatce nie wystartowałoby animacji od nowa i drugie kliknięcie „Zapisz"
    // nie dałoby żadnej reakcji.
    void target.offsetWidth;
    target.classList.add(SHAKE_CLASS);

    target.addEventListener(
      'animationend',
      () => target.classList.remove(SHAKE_CLASS),
      { once: true }
    );
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
